/**
 * Emoji picker for composers (t159u): a smiley button that opens a searchable, categorised grid with recents.
 * Picking inserts at the caret and keeps focus in the composer. Keyboard: arrows move, Enter picks, Escape closes.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Smile } from 'lucide-react';
import { EMOJI_GROUPS } from './data';

const RECENT_KEY = 'emoji.recent';
const COLS = 8;
const recent = (): string[] => { try { return (JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[]).slice(0, 16); } catch { return []; } };
const remember = (e: string) => localStorage.setItem(RECENT_KEY, JSON.stringify([e, ...recent().filter(x => x !== e)].slice(0, 16)));

/** Insert text at the textarea's caret (replacing any selection); returns the new value and caret. */
export function insertAtCaret(el: HTMLTextAreaElement | HTMLInputElement | null, value: string, insert: string) {
  const start = el?.selectionStart ?? value.length, end = el?.selectionEnd ?? value.length;
  return { value: value.slice(0, start) + insert + value.slice(end), caret: start + insert.length };
}
/** Put the caret back after React re-renders the new value. */
export function restoreCaret(el: HTMLTextAreaElement | HTMLInputElement | null, caret: number) {
  requestAnimationFrame(() => { if (!el) return; el.focus(); el.setSelectionRange(caret, caret); });
}

export function EmojiButton({ onPick, className, placement = 'top', children, label = 'Insert emoji', title = 'Emoji', keepFocus = true }: {
  onPick: (emoji: string) => void; className?: string; placement?: 'top' | 'bottom';
  /** t635u: custom trigger content (e.g. the current icon) instead of the smiley. */
  children?: React.ReactNode; label?: string; title?: string;
  /** Composers keep the caret in their textarea; pickers used on their own let the button take focus. */
  keepFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    setQ(''); setActive(0);
    requestAnimationFrame(() => search.current?.focus());
    const out = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', out);
    return () => document.removeEventListener('mousedown', out);
  }, [open]);
  const groups = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t) return [{ id: 'results', label: 'Results', items: EMOJI_GROUPS.flatMap(g => g.items).filter(([, w]) => w.split(' ').some(x => x.startsWith(t)) || w.includes(t)) }];
    const r = recent();
    return [...(r.length ? [{ id: 'recent', label: 'Recently used', items: r.map(e => [e, ''] as [string, string]) }] : []), ...EMOJI_GROUPS];
  }, [q, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const flat = groups.flatMap(g => g.items.map(i => i[0]));
  const pick = (e: string) => { remember(e); onPick(e); setOpen(false); };
  const onKey = (e: React.KeyboardEvent) => {
    const move = (d: number) => { e.preventDefault(); setActive(a => Math.max(0, Math.min(flat.length - 1, a + d))); };
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    else if (e.key === 'ArrowRight') move(1); else if (e.key === 'ArrowLeft') move(-1);
    else if (e.key === 'ArrowDown') move(COLS); else if (e.key === 'ArrowUp') move(-COLS);
    else if (e.key === 'Enter' && flat[active]) { e.preventDefault(); pick(flat[active]); }
  };
  let n = -1;
  return (
    <div ref={box} className="relative inline-flex">
      <button type="button" title={title} aria-label={label} aria-haspopup="dialog" aria-expanded={open} data-testid="emoji-button"
        onMouseDown={e => { if (keepFocus) e.preventDefault(); }} onClick={() => setOpen(o => !o)}
        className={clsx('rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200', open && 'bg-slate-100 dark:bg-slate-800', className)}>
        {children ?? <Smile className="h-4 w-4" />}
      </button>
      {open && (
        <div role="dialog" aria-label="Emoji picker" data-testid="emoji-picker" onKeyDown={onKey}
          className={clsx('absolute left-0 z-50 w-[19rem] rounded-xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-700 dark:bg-slate-900', placement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1')}>
          <input ref={search} value={q} onChange={e => { setQ(e.target.value); setActive(0); }} placeholder="Search emoji" aria-label="Search emoji"
            className="mb-2 w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-sm focus:border-slate-400 focus:outline-none dark:border-slate-700 dark:bg-slate-950" />
          <div className="scroll-thin max-h-60 overflow-y-auto pr-1">
            {groups.map(g => (
              <div key={g.id} className="mb-1.5">
                <p className="px-1 pb-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{g.label}</p>
                <div className="grid grid-cols-8" role="listbox" aria-label={g.label}>
                  {g.items.map(([e, words]) => { n++; const i = n; return (
                    <button key={`${g.id}-${e}`} type="button" role="option" aria-selected={i === active} title={words.split(' ')[0] || e} aria-label={words.split(' ')[0] || e}
                      onMouseDown={ev => ev.preventDefault()} onClick={() => pick(e)} onMouseEnter={() => setActive(i)}
                      className={clsx('grid h-8 w-8 place-items-center rounded-md text-xl leading-none', i === active ? 'bg-slate-100 dark:bg-slate-800' : 'hover:bg-slate-100 dark:hover:bg-slate-800')}>{e}</button>
                  ); })}
                </div>
              </div>
            ))}
            {!flat.length && <p className="px-1 py-4 text-center text-sm text-slate-500">No emoji found</p>}
          </div>
        </div>
      )}
    </div>
  );
}
